/**
 * @name ForgotPasswordPage.jsx
 * @description 비밀번호 찾기 페이지
 * - 아이디 + 이름 + 전화번호 입력 후 3가지 일치 시 임시 비밀번호 화면에 표시
 * - 임시 비밀번호로 로그인 후 비밀번호 변경 화면으로 강제 이동
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { post } from '@utils/axios_api';
import { toast } from 'react-toastify';
import './Login.css';

const ForgotPasswordPage = () => {
  const [form, setForm] = useState({ id: '', name: '', phoneNo: '' });
  const [tempPassword, setTempPassword] = useState(null);  // 발급된 임시 비밀번호
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitized = name === 'phoneNo' ? value.replace(/\D/g, '') : value;
    setForm((prev) => ({ ...prev, [name]: sanitized }));
    setTempPassword(null);
    setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.id || !form.name || !form.phoneNo) {
      setErrorMsg('아이디, 이름, 전화번호를 모두 입력해 주세요.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await post('/auth/forgot-password', form, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.result.success) {
        setTempPassword(response.result.tempPassword);
        setErrorMsg('');
      } else {
        setTempPassword(null);
        setErrorMsg(response.result.message || '입력하신 정보와 일치하는 계정이 없습니다.');
      }
    } catch (err) {
      // 서버/네트워크 에러는 axios interceptor가 이미 toast로 표시하고 throw함
      // → 여기서 setErrorMsg 추가 시 toast + 인라인 에러 이중 표시되므로 console만 처리
      console.error('비밀번호 찾기 오류:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // 클립보드 복사 (HTTPS 환경 필요, 실패 시 안내)
  const handleCopy = () => {
    if (!tempPassword) return;
    navigator.clipboard.writeText(tempPassword)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        toast.warn('복사에 실패했습니다. 비밀번호를 직접 메모해 주세요: ' + tempPassword);
      });
  };

  return (
    <div className="main-container">
      <div className="login-container">
        {/* 로고 */}
        <div className="logo">
          <Link to="/">
            <img src="/img/logo.png" alt="회사 로고" style={{ cursor: 'pointer' }} />
          </Link>
        </div>

        <div className="login-form">
          <h1>비밀번호 찾기</h1>
          <p style={{ color: '#666', marginBottom: '20px', fontSize: '0.9rem' }}>
            가입 시 등록한 아이디, 이름, 전화번호를 입력해 주세요.
          </p>

          {/* 임시 비밀번호 결과 표시 */}
          {tempPassword && (
            <div style={{
              background: '#fff8e1',
              border: '1px solid #FFC107',
              borderRadius: '6px',
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, color: '#555', fontSize: '0.9rem' }}>임시 비밀번호가 발급되었습니다.</p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', margin: '10px 0' }}>
                <span style={{ fontWeight: 'bold', fontSize: '1.4rem', color: '#e65100', letterSpacing: '2px' }}>
                  {tempPassword}
                </span>
                <button
                  type="button"
                  onClick={handleCopy}
                  style={{
                    padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer',
                    border: '1px solid #FFC107', borderRadius: '4px',
                    background: copied ? '#FFC107' : '#fff', color: '#555'
                  }}
                >
                  {copied ? '복사됨!' : '복사'}
                </button>
              </div>
              <p style={{ margin: 0, color: '#e65100', fontSize: '0.85rem' }}>
                ⚠️ 로그인 후 반드시 비밀번호를 변경해 주세요.
              </p>
            </div>
          )}

          {/* 에러 메시지 */}
          {errorMsg && (
            <p className="error-message red" style={{ marginBottom: '12px' }}>{errorMsg}</p>
          )}

          {/* 입력 폼 - 임시 비밀번호 발급 후 숨김 */}
          {!tempPassword && (
            <form onSubmit={handleSubmit}>
              <div className="login_input_wrap">
                <input
                  type="text"
                  name="id"
                  placeholder="아이디를 입력해 주세요."
                  value={form.id}
                  onChange={handleChange}
                  maxLength="30"
                  style={{ width: '100%', padding: '12px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '1rem' }}
                />
                <input
                  type="text"
                  name="name"
                  placeholder="이름을 입력해 주세요."
                  value={form.name}
                  onChange={handleChange}
                  maxLength="20"
                  style={{ width: '100%', padding: '12px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '1rem' }}
                />
                <input
                  type="text"
                  name="phoneNo"
                  placeholder="전화번호를 입력해 주세요. (예: 01012345678)"
                  value={form.phoneNo}
                  onChange={handleChange}
                  maxLength="11"
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '1rem' }}
                />
                <p style={{ fontSize: '11px', color: '#888', margin: '3px 0 0' }}>- 없이 숫자만 입력하세요.</p>
              </div>
              <button
                type="submit"
                className="login-button"
                disabled={isLoading}
                style={{ marginTop: '16px' }}
              >
                {isLoading ? '조회 중...' : '임시 비밀번호 발급'}
              </button>
            </form>
          )}

          {/* 발급 후 로그인 버튼 - Link를 버튼 스타일로 직접 처리 (<a> 안에 <button> 중첩 방지) */}
          {tempPassword && (
            <Link
              to="/login"
              className="login-button"
              style={{ marginTop: '12px', background: '#1976D2', display: 'block', textAlign: 'center', textDecoration: 'none' }}
            >
              로그인하러 가기
            </Link>
          )}

          {/* 하단 링크 */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '20px', fontSize: '0.9rem' }}>
            <Link to="/login" style={{ color: '#555' }}>로그인</Link>
            <span style={{ color: '#ddd' }}>|</span>
            <Link to="/forgot-id" style={{ color: '#555' }}>아이디 찾기</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ForgotPasswordPage };
